/**
 * PLM — QC mode: the QC Results list (prototype v13 `renderListView` for
 * `MOD.m === 'qc'`, 2026-09-28).
 *
 * One row per re-read — QC ID · QC Batch ID · QC Type · Sample · Analyst(s) ·
 * Date · Result — with type chips, "+ New QC", Refresh, QC Charts and Export.
 * Double-click opens the batch (Enter mode) so the pair can be reviewed on its
 * QC Result View tab. The rows and their verdicts come from the lab-wide QC
 * history through plmWorkflow.qcRecords, which applies the v13 visibility and
 * blind rules for the signed-in user. Reference rows are the Daily QC reads
 * (daily blind reference-slide reads — double-click opens the Daily QC screen).
 * The lab's pre-go-live QC logs are not imported (owner, 2026-09-28).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '../../../auth/store';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useHotkeys, formatCombo, effectiveCombo } from '../../../components/hotkeys';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { fmtLocalDate } from '../../../utils/datetime';
import {
  fetchPlmDailyQcList, fetchPlmLookups, fetchPlmQcHistory, type PlmDailyQcRow, type PlmQcHistoryRow,
} from './plm.api';
import { dailyQcRecords } from './plmDailyQc';
import { PlmQcChartsPage } from './PlmQcChartsPage';
import PlmNewQcDialog from './PlmNewQcDialog';
import { qcRecords, type QcRecord } from './plmWorkflow';
import './plm.css';

type Chip = 'all' | QcRecord['type'];
const CHIPS: { id: Chip; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'Reference', label: 'Reference' },
  { id: 'Intra-dup', label: 'Intra-dup' },
  { id: 'Inter-dup', label: 'Inter-dup' },
];

/** The Result cell: the verdict, why a dup broke the analyst rule (an Inter
 *  dup must be read by another analyst, an Intra dup by the same one — owner,
 *  2026-09-29), and whether it was read blind. */
const resultText = (r: QcRecord) => {
  if (r.result === 'Blind') return '🔒 Blind';
  if (r.ruleBreak) return `${r.result} · ${r.ruleBreak}`;
  return r.notBlind && r.result !== 'Pending' ? `${r.result} (not blind)` : r.result;
};

interface Props {
  /** Open a batch in Enter mode, optionally on one layer. */
  onOpenBatch: (batchOid: string, sequenceOid?: string) => void;
  /** Open the Daily QC screen on a record, or (null) on the slide picker. */
  onOpenDailyQc: (oid: string | null) => void;
}

export default function PlmQcListPage({ onOpenBatch, onOpenDailyQc }: Props) {
  const me = useAuthStore(s => s.user?.id ?? null);
  const [rows, setRows] = useState<PlmQcHistoryRow[]>([]);
  const [daily, setDaily] = useState<PlmDailyQcRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [chip, setChip] = useState<Chip>('all');
  const [selected, setSelected] = useState<string | null>(null);
  const [newQcOpen, setNewQcOpen] = useState(false);
  const [chartsOpen, setChartsOpen] = useState(false);
  // The QC dup switch (AI_AppSettings 'PlmDupSubmitButtons', 2026-10-17): 'On' keeps the
  // separate dup steps; otherwise the SIMPLE flow — blind rows read "Blind" for everyone
  // but the original analyst, and New QC offers no Inter-dup "start". Until the lookups
  // answer, today's flow ('On') is assumed, as for an API that does not send the switch.
  const [dupSteps, setDupSteps] = useState(true);
  useEffect(() => {
    void fetchPlmLookups().then(lk => setDupSteps(lk.dupSubmitButtons !== false)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const t0 = performance.now();
    setLoading(true);
    try {
      // The rows this user may see, decided on the server (owner 2026-10-17, G5 parity).
      const [h, d] = await Promise.all([fetchPlmQcHistory(undefined, undefined, 'records'), fetchPlmDailyQcList()]);
      setRows(h);
      setDaily(d);
    } finally {
      setLoading(false);
      setLoadMs(performance.now() - t0);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const all = useMemo(() => [...dailyQcRecords(daily), ...qcRecords(rows, me, !dupSteps)]
    .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? '')), [daily, rows, me, dupSteps]);
  const shown = useMemo(() => (chip === 'all' ? all : all.filter(r => r.type === chip)), [all, chip]);

  const columns = useMemo<AppColumn<QcRecord>[]>(() => [
    { field: 'qcId', header: 'QC ID', width: 150, text: r => r.qcId },
    { field: 'batchId', header: 'QC Batch ID', width: 150, text: r => r.batchId ?? '' },
    {
      field: 'type', header: 'QC Type', width: 110, text: r => r.type,
      render: r => <span className={`plm-qctag ${r.type === 'Reference' ? 'is-ref'
        : r.type === 'Intra-dup' ? 'is-intra' : 'is-inter'}`}>{r.type.toUpperCase()}</span>,
    },
    { field: 'sample', header: 'Sample / Slide', width: 170, text: r => r.sample },
    { field: 'analysts', header: 'Analyst(s)', width: 200, text: r => r.analysts },
    { field: 'date', header: 'Date', width: 110, text: r => (r.date ? fmtLocalDate(r.date) : '') },
    { field: 'result', header: 'Result', width: 150, type: 'status', text: resultText },
  ], []);

  // QC Charts on the keyboard (owner 2026-10-19); New QC / Refresh / Export come with the toolbar.
  useHotkeys([{ combo: 'alt+c', label: 'QC Charts', group: 'PLM QC', order: 1, disabled: chartsOpen, run: () => setChartsOpen(true) }]);

  if (chartsOpen) return <PlmQcChartsPage onBack={() => setChartsOpen(false)} />;

  const count = (c: Chip) => (c === 'all' ? all.length : all.filter(r => r.type === c).length);

  return (
    <div className="plm-root">
      <div className="dp-breadcrumb">
        <span>Home</span><i className="ti ti-chevron-right" />
        <span>Analysis</span><i className="ti ti-chevron-right" />
        <span>PLM</span><i className="ti ti-chevron-right" />
        <span className="dp-bc-active">QC Results</span>
      </div>
      <FormTitle pageId="plm" title="PLM – QC Results" loadMs={loadMs} loading={loading} auditFormName="PLM" />

      <AppToolbar
        left={
          <span className="plm-chips" role="tablist" aria-label="QC type">
            {CHIPS.map(c => (
              <button key={c.id} type="button" role="tab" aria-selected={chip === c.id}
                className={`plm-chip${chip === c.id ? ' is-on' : ''}`}
                onClick={() => setChip(c.id)}>
                {c.label} <b>{count(c.id)}</b>
              </button>
            ))}
          </span>
        }
        onNew={() => setNewQcOpen(true)}
        newLabel="New QC"
        beforeRefresh={
          <button className="ca-btn" onClick={() => setChartsOpen(true)} title={`PLM Statistical QC charts (${formatCombo(effectiveCombo('alt+c'))})`}>
            <i className="ti ti-chart-dots" aria-hidden="true" /> QC Charts
          </button>
        }
        onRefresh={() => { void load(); }}
        refreshing={loading}
        exportData={{
          filename: 'PLM-QC-Results',
          headers: ['QC ID', 'QC Batch ID', 'QC Type', 'Sample / Slide', 'Analyst(s)', 'Date', 'Result'],
          rows: shown.map(r => [r.qcId, r.batchId ?? '', r.type, r.sample, r.analysts,
            r.date ? fmtLocalDate(r.date) : '', resultText(r)]),
        }}
      />

      <div className="plm-list">
        <AppDataGrid
          rows={shown}
          columns={columns}
          getRowId={r => r.key}
          selectedRowId={selected}
          onRowClick={r => setSelected(r.key)}
          onRowDoubleClick={r => {
            if (r.type === 'Reference') onOpenDailyQc(r.key.replace(/^dq:/, ''));
            else if (r.batchOid) onOpenBatch(r.batchOid, r.key);
          }}
          loading={loading}
          defaultPageSize={50}
          emptyText="No QC records yet. Start one with + New QC."
        />
      </div>
      <p className="plm-hint">
        Double-click a record to open it — a Reference read opens the Daily QC screen, a dup
        opens its batch (the pair is on the QC Result View tab). {dupSteps
          ? "Another analyst's Inter-dup appears here once they submit it."
          : 'A pair reads Blind until its batch is submitted, except for the original analyst.'}
      </p>

      <PlmNewQcDialog
        open={newQcOpen}
        onClose={() => setNewQcOpen(false)}
        me={me}
        dupSteps={dupSteps}
        onCreated={(b, seq) => onOpenBatch(b, seq)}
        onReference={() => onOpenDailyQc(null)}
      />
    </div>
  );
}
